import { Link } from "react-router-dom";
import { useTheme } from "@/App";
import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";

const sections = [
  {
    title: "Acceptance",
    content: (
      <p>By creating a FirstHold account, you agree to these Terms. If you do not agree, do not use the service.</p>
    ),
  },
  {
    title: "The Service",
    content: (
      <p>FirstHold provides tools for freelance professionals to track bookings, manage availability, generate invoices, and estimate tax liability. The service is provided "as is" for informational and organizational purposes.</p>
    ),
  },
  {
    title: "Tax Estimates Disclaimer",
    content: (
      <p>Tax estimates provided by FirstHold are approximations based on information you enter and publicly available tax data. They are not professional tax advice. Always consult a qualified tax professional before filing. FirstHold accepts no liability for tax decisions made based on estimates provided by the platform.</p>
    ),
  },
  {
    title: "Eligible Users",
    content: (
      <p>You must be at least 16 years old and legally able to enter contracts in your jurisdiction. The service is intended for freelance professionals and sole traders.</p>
    ),
  },
  {
    title: "Your Account",
    content: (
      <ul>
        <li>You are responsible for maintaining the security of your account credentials</li>
        <li>You are responsible for all activity under your account</li>
        <li>Notify us immediately at <a href="mailto:hello@firsthold.io" className="underline underline-offset-2">hello@firsthold.io</a> if you suspect unauthorized access</li>
      </ul>
    ),
  },
  {
    title: "Acceptable Use",
    content: (
      <>
        <p>You may not:</p>
        <ul>
          <li>Use the service for any unlawful purpose</li>
          <li>Attempt to access other users' data</li>
          <li>Reverse engineer or attempt to extract the source code</li>
          <li>Upload malicious files or attempt to disrupt the service</li>
          <li>Create multiple accounts to abuse free trial periods</li>
        </ul>
      </>
    ),
  },
  {
    title: "Subscription and Billing",
    content: (
      <ul>
        <li><strong>Free trial</strong> — 14 days with full access. No credit card required to start.</li>
        <li><strong>Paid plans</strong> — billed monthly via Stripe. You authorize recurring charges at signup.</li>
        <li><strong>Cancellation</strong> — you may cancel at any time. Access continues until the end of the billing period. No refunds for partial periods.</li>
        <li><strong>Plan changes</strong> — upgrades take effect immediately; downgrades take effect at the next billing cycle.</li>
      </ul>
    ),
  },
  {
    title: "Your Data",
    content: (
      <p>You own your data. We do not claim any rights over the content you enter. You can export or delete your data at any time. See our <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link> for details on how we handle your data.</p>
    ),
  },
  {
    title: "Invoices and Documents",
    content: (
      <p>Invoices generated by FirstHold are based entirely on information you provide. You are responsible for the accuracy of invoice content, compliance with local invoicing regulations, and any tax obligations arising from your business.</p>
    ),
  },
  {
    title: "Intellectual Property",
    content: (
      <p>FirstHold, its design, and its software are owned by Paolo Cogliati LLC and protected by applicable intellectual property laws. You may not reproduce or redistribute any part of the service.</p>
    ),
  },
  {
    title: "Limitation of Liability",
    content: (
      <p>To the maximum extent permitted by law, FirstHold shall not be liable for any indirect, incidental, special, or consequential damages arising from use of the service, including but not limited to loss of earnings, data loss, or tax penalties. Our total liability to you shall not exceed the amount you paid us in the 12 months preceding the claim.</p>
    ),
  },
  {
    title: "Indemnification",
    content: (
      <p>You agree to indemnify FirstHold against any claims, damages, or expenses arising from your use of the service or violation of these Terms.</p>
    ),
  },
  {
    title: "Governing Law",
    content: (
      <p>These Terms are governed by the laws of the State of California, United States, without regard to conflict of law provisions. EU users retain the right to bring claims before their local courts under applicable consumer protection laws.</p>
    ),
  },
  {
    title: "Changes to Terms",
    content: (
      <p>We will notify users of material changes via email or in-app notice at least 14 days before changes take effect. Continued use after that date constitutes acceptance.</p>
    ),
  },
  {
    title: "Contact",
    content: (
      <p><a href="mailto:hello@firsthold.io" className="underline underline-offset-2">hello@firsthold.io</a></p>
    ),
  },
];

export default function TermsPage() {
  const { isDark } = useTheme();

  const pageBg   = isDark ? "bg-gradient-to-b from-[#0f1419] via-[#131820] to-[#0a0e12] text-white" : "bg-[#E3E8F1] text-[#1A1F36]";
  const cardBg   = isDark ? "bg-white/5 border-white/10" : "bg-white/80 border-white";
  const mutedTxt = isDark ? "text-white/50" : "text-[#1A1F36]/50";
  const bodyTxt  = isDark ? "text-white/80" : "text-[#1A1F36]/80";
  const hoverBg  = isDark ? "hover:text-white/70" : "hover:text-[#1A1F36]/70";

  return (
    <div className={cn("min-h-screen font-sans", pageBg)}>
      <main className="max-w-3xl mx-auto px-6 py-12 pb-24">

        {/* Back */}
        <Link
          to="/login"
          className={cn("inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] mb-12 transition-opacity hover:opacity-60", mutedTxt)}
        >
          <ArrowLeft size={12} />
          FirstHold
        </Link>

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-3">Terms of Service</h1>
          <p className={cn("text-sm font-medium", mutedTxt)}>Last updated July 1, 2026</p>
        </div>

        {/* Sections */}
        <div className={cn("rounded-[2rem] border divide-y", cardBg, isDark ? "divide-white/10" : "divide-black/5")}>
          {sections.map((s, i) => (
            <section key={i} className="px-8 py-8">
              <h2 className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 opacity-40">{i + 1}. {s.title}</h2>
              <div className={cn(
                "text-sm leading-relaxed space-y-3",
                bodyTxt,
                "[&_ul]:space-y-2 [&_ul]:list-disc [&_ul]:pl-5",
                "[&_li]:leading-relaxed",
                "[&_strong]:font-bold [&_strong]:opacity-100",
                "[&_a]:underline [&_a]:underline-offset-2",
                `[&_a]:${hoverBg}`
              )}>
                {s.content}
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center pt-12">
          <p className={cn("text-[10px] font-bold uppercase tracking-[0.2em]", mutedTxt)}>
            © 2026 FirstHold &nbsp;·&nbsp;
            <Link to="/privacy" className="hover:opacity-60 transition-opacity">Privacy Policy</Link>
          </p>
        </div>

      </main>
    </div>
  );
}
